import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from "@tanstack/react-table";
import {
  MdEdit,
  MdDelete,
  MdPerson,
  MdClose,
  MdDownload,
  MdPeople,
} from "react-icons/md";
import {
  BiaFitUser,
  SubscriptionProvider,
  SubscriptionStatus,
} from "../../types/user";
import Card from "../card";
import FilterDropdown from "../common/FilterDropdown";
import ActiveFilterBadge from "../common/ActiveFilterBadge";
import { useChallenges } from "../../hooks/useChallenges";
import DataTable from "../common/DataTable";
import SearchForm from "../common/SearchForm";
import LoadingSpinner from "../common/LoadingSpinner";
import { ActiveChallengeBadges, UserStatusBadge } from "./userBadges";

interface UsersTableProps {
  users: BiaFitUser[];
  loading: boolean;
  onEdit: (user: BiaFitUser) => void;
  onDelete: (user: BiaFitUser) => void;
  onView: (user: BiaFitUser) => void;
  onSearch?: (searchTerm: string) => void;
  onClearSearch?: () => void;
  searchTerm?: string;
  isSearchMode?: boolean;
  subscriptionProvider?: SubscriptionProvider;
  subscriptionStatus?: SubscriptionStatus;
  isChallengeParticipant?: boolean;
  challengeId?: number;
  withTrashed?: boolean;
  onSubscriptionProviderChange?: (
    provider: SubscriptionProvider | undefined
  ) => void;
  onSubscriptionStatusChange?: (status: SubscriptionStatus | undefined) => void;
  onChallengeParticipantChange?: (isParticipant: boolean | undefined) => void;
  onChallengeChange?: (challengeId: number | undefined) => void;
  onWithTrashedChange?: (withTrashed: boolean) => void;
  onDownloadCsv?: () => void;
  downloadingCsv?: boolean;
}

const columnHelper = createColumnHelper<BiaFitUser>();

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  loading,
  onEdit,
  onDelete,
  onView,
  onSearch,
  onClearSearch,
  searchTerm = "",
  isSearchMode = false,
  subscriptionProvider,
  subscriptionStatus,
  isChallengeParticipant,
  challengeId,
  withTrashed,
  onSubscriptionProviderChange,
  onSubscriptionStatusChange,
  onChallengeParticipantChange,
  onChallengeChange,
  onWithTrashedChange,
  onDownloadCsv,
  downloadingCsv = false,
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const { challenges } = useChallenges();

  const subscriptionProviderOptions = [
    { value: "stripe", label: "Stripe (Web/Android)" },
    { value: "revenue_cat", label: "RevenueCat (iOS)" },
    { value: "trial", label: "Trial" },
  ];

  // Filter subscription status options based on provider
  const subscriptionStatusOptions =
    subscriptionProvider === "trial"
      ? [
          { value: "active", label: "Active" },
          { value: "expired", label: "Expired" },
        ]
      : [
          { value: "active", label: "Active" },
          { value: "cancelled", label: "Cancelled" },
          { value: "expired", label: "Expired" },
          { value: "none", label: "No Subscription" },
        ];

  const challengeParticipantOptions = [
    { value: "true", label: "Participants" },
    { value: "false", label: "Non-Participants" },
  ];

  const userStatusOptions = [{ value: "active_only", label: "Active Only" }];

  const challengeOptions = challenges.map((challenge) => ({
    value: challenge.id.toString(),
    label: `${challenge.name} (${challenge.conducted_at_year})`,
  }));

  React.useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && localSearchTerm.trim()) {
      onSearch(localSearchTerm.trim());
    }
  };

  const handleClearSearch = () => {
    setLocalSearchTerm("");
    if (onClearSearch) {
      onClearSearch();
    }
  };

  const handleRowClick = (event: React.MouseEvent, user: BiaFitUser) => {
    const target = event.target as HTMLElement;
    if (target.closest('[data-stop-propagation="true"]')) {
      return;
    }
    if (user.deleted_at) {
      return;
    }
    onView(user);
  };

  const columns = useMemo<ColumnDef<BiaFitUser, any>[]>(
    () => [
      columnHelper.accessor("first_name", {
        id: "name",
        header: () => "Name",
        cell: (info) => {
          const user = info.row.original;
          const fullName = `${user.first_name || ""} ${
            user.last_name || ""
          }`.trim();
          return (
            <div className="flex items-center">
              <div className="h-12 w-12 flex-shrink-0">
                {user.profile_image?.full_path ? (
                  <img
                    src={user.profile_image.full_path}
                    alt={fullName || user.email}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-biafit-primary text-white">
                    <MdPerson className="h-6 w-6" />
                  </div>
                )}
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-navy-700 dark:text-white">
                  {fullName || "N/A"}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-300">
                  {user.email}
                </div>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("is_active", {
        header: () => "Status",
        cell: (info) => {
          const user = info.row.original;
          return (
            <div className="flex flex-wrap items-center gap-2">
              <UserStatusBadge user={user} />
              <ActiveChallengeBadges user={user} />
            </div>
          );
        },
      }),
      columnHelper.accessor("created_at", {
        header: () => "Joined",
        cell: (info) => {
          const date = new Date(info.getValue());
          return (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {date.toLocaleDateString()}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => "Actions",
        cell: (info) => {
          const user = info.row.original;
          const isDeleted = Boolean(user.deleted_at);
          return (
            <div className="flex space-x-2">
              <button
                data-stop-propagation="true"
                onClick={(event) => {
                  event.stopPropagation();
                  if (isDeleted) return;
                  onEdit(user);
                }}
                disabled={isDeleted}
                className={`text-gray-600 transition-colors dark:text-gray-400 ${
                  isDeleted
                    ? "cursor-not-allowed opacity-50"
                    : "hover:text-blue-600 dark:hover:text-blue-400"
                }`}
                title="Edit User"
              >
                <MdEdit className="h-5 w-5" />
              </button>
              <button
                data-stop-propagation="true"
                onClick={(event) => {
                  event.stopPropagation();
                  if (isDeleted) return;
                  onDelete(user);
                }}
                disabled={isDeleted}
                className={`text-gray-600 transition-colors dark:text-gray-400 ${
                  isDeleted
                    ? "cursor-not-allowed opacity-50"
                    : "hover:text-red-600 dark:hover:text-red-400"
                }`}
                title="Delete User"
              >
                <MdDelete className="h-5 w-5" />
              </button>
            </div>
          );
        },
      }),
    ],
    [onEdit, onDelete]
  );

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return (
      <Card extra="w-full h-full p-3 md:p-6 sm:overflow-x-auto">
        <LoadingSpinner />
      </Card>
    );
  }

  const hasActiveFilters =
    subscriptionProvider ||
    subscriptionStatus ||
    isSearchMode ||
    isChallengeParticipant !== undefined ||
    challengeId !== undefined ||
    withTrashed === false;

  return (
    <Card extra="w-full h-full p-3 md:p-6 sm:overflow-x-auto">
      <div className="relative flex flex-col space-y-4 pb-8">
        <div className="flex flex-col items-start justify-between space-y-4 sm:flex-row sm:items-center sm:space-y-0">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            Users Table
          </div>

          <div className="w-full sm:w-auto">
            <SearchForm
              value={localSearchTerm}
              onChange={setLocalSearchTerm}
              onSubmit={handleSearchSubmit}
              onClear={handleClearSearch}
              placeholder="Search users..."
              hasSearched={isSearchMode}
              loading={loading}
            />
          </div>
        </div>

        {/* Filter Controls */}
        <div className="border-t-[1px] border-solid border-gray-200 pt-4 dark:border-gray-700">
          <h3 className="mb-2 text-[15px] font-medium text-gray-700 dark:text-gray-400">
            Filters:
          </h3>

          <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col flex-wrap gap-3 sm:flex-row">
              <div className="w-full sm:w-auto">
                <FilterDropdown
                  label="Subscription Provider"
                  options={subscriptionProviderOptions}
                  value={subscriptionProvider}
                  onChange={(value) =>
                    onSubscriptionProviderChange?.(
                      value as SubscriptionProvider | undefined
                    )
                  }
                  placeholder="All Providers"
                />
              </div>
              {subscriptionProvider !== "trial" && (
                <div className="w-full sm:w-auto">
                  <FilterDropdown
                    label="Subscription Status"
                    options={subscriptionStatusOptions}
                    value={subscriptionStatus}
                    onChange={(value) =>
                      onSubscriptionStatusChange?.(
                        value as SubscriptionStatus | undefined
                      )
                    }
                    placeholder="All Statuses"
                  />
                </div>
              )}
              {subscriptionProvider === "trial" && (
                <div className="w-full sm:w-auto">
                  <FilterDropdown
                    label="Subscription Status"
                    options={subscriptionStatusOptions}
                    value={subscriptionStatus}
                    onChange={(value) =>
                      onSubscriptionStatusChange?.(
                        value as SubscriptionStatus | undefined
                      )
                    }
                    placeholder="All Statuses"
                  />
                </div>
              )}
              <div className="w-full sm:w-auto">
                <FilterDropdown
                  label="Challenge Participation"
                  options={challengeParticipantOptions}
                  value={
                    isChallengeParticipant === true
                      ? "true"
                      : isChallengeParticipant === false
                      ? "false"
                      : undefined
                  }
                  onChange={(value) =>
                    onChallengeParticipantChange?.(
                      value === "true"
                        ? true
                        : value === "false"
                        ? false
                        : undefined
                    )
                  }
                  placeholder="All Users"
                />
              </div>
              <div className="w-full sm:w-auto">
                <FilterDropdown
                  label="Challenge"
                  options={challengeOptions}
                  value={challengeId?.toString()}
                  onChange={(value) =>
                    onChallengeChange?.(value ? parseInt(value, 10) : undefined)
                  }
                  placeholder="No Selection"
                />
              </div>
              <div className="w-full sm:w-auto">
                <FilterDropdown
                  label="Include Deleted"
                  options={userStatusOptions}
                  value={withTrashed === false ? "active_only" : undefined}
                  onChange={(value) =>
                    onWithTrashedChange?.(value !== "active_only")
                  }
                  placeholder="Include Deleted"
                />
              </div>
            </div>

            {onDownloadCsv && (
              <button
                onClick={onDownloadCsv}
                disabled={downloadingCsv}
                className="inline-flex w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 sm:w-auto"
              >
                <MdDownload className="mr-2 h-4 w-4" />
                {downloadingCsv ? "Downloading..." : "Download CSV"}
              </button>
            )}
          </div>
        </div>

        {/* Active Filters */}
        {hasActiveFilters && (
          <div className="text-sm text-gray-700 dark:text-gray-400">
            <p className="mb-1 text-[15px] font-medium">Active filters:</p>
            <div className="flex flex-wrap items-center gap-2">
              {isSearchMode && (
                <button
                  onClick={handleClearSearch}
                  className="group flex items-center gap-1.5 rounded bg-blue-100 px-2 py-1 font-medium text-blue-800 transition-colors hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                >
                  <span>Search: "{searchTerm}"</span>
                  <MdClose className="h-3.5 w-3.5 transition-transform group-hover:scale-110" />
                </button>
              )}
              {subscriptionProvider && (
                <ActiveFilterBadge
                  label={`Subscription Provider: ${
                    subscriptionProviderOptions.find(
                      (o) => o.value === subscriptionProvider
                    )?.label
                  }`}
                  colorScheme="purple"
                  onClear={() => onSubscriptionProviderChange?.(undefined)}
                />
              )}
              {subscriptionStatus && (
                <ActiveFilterBadge
                  label={`Subscription Status: ${
                    subscriptionStatusOptions.find(
                      (o) => o.value === subscriptionStatus
                    )?.label
                  }`}
                  colorScheme="green"
                  onClear={() => onSubscriptionStatusChange?.(undefined)}
                />
              )}
              {isChallengeParticipant !== undefined && (
                <ActiveFilterBadge
                  label={`Challenge Participation: ${
                    challengeParticipantOptions.find(
                      (o) =>
                        o.value === (isChallengeParticipant ? "true" : "false")
                    )?.label
                  }`}
                  colorScheme="blue"
                  onClear={() => onChallengeParticipantChange?.(undefined)}
                />
              )}
              {challengeId && (
                <ActiveFilterBadge
                  label={`Challenge: ${
                    challengeOptions.find(
                      (o) => o.value === challengeId.toString()
                    )?.label
                  }`}
                  colorScheme="pink"
                  onClear={() => onChallengeChange?.(undefined)}
                />
              )}
              {withTrashed === false && (
                <button
                  onClick={() => onWithTrashedChange?.(true)}
                  className="group flex items-center gap-1.5 rounded bg-teal-100 px-2 py-1 font-medium text-teal-800 transition-colors hover:bg-teal-200 dark:bg-teal-900 dark:text-teal-200 dark:hover:bg-teal-800"
                >
                  <span>Include Deleted: Active Only</span>
                  <MdClose className="h-3.5 w-3.5 transition-transform group-hover:scale-110" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <DataTable
        table={table}
        tableKey={`users-table-${isSearchMode ? "search" : "regular"}`}
        isEmpty={users.length === 0}
        empty={{ icon: MdPeople, title: "No users found" }}
        onRowClick={handleRowClick}
        rowClassName={(user: BiaFitUser) =>
          user.deleted_at ? "cursor-not-allowed opacity-60" : ""
        }
        mobile={users.map((user) => {
          const fullName = `${user.first_name || ""} ${
            user.last_name || ""
          }`.trim();
          const initials = (fullName || user.email || "U")
            .split(" ")
            .map((p) => p.charAt(0))
            .join("")
            .slice(0, 2)
            .toUpperCase();
          const isDeleted = Boolean(user.deleted_at);
          return (
            <div
              key={user.id}
              className={`rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-navy-900 md:p-4 ${
                isDeleted ? "cursor-not-allowed opacity-60" : "cursor-pointer"
              }`}
              onClick={() => {
                if (isDeleted) return;
                onView(user);
              }}
            >
              <div className="mb-2 flex items-start justify-between">
                <div className="flex max-w-[calc(100%-30px)] items-start space-x-3">
                  <div className="h-12 w-12 flex-shrink-0">
                    {user.profile_image?.full_path ? (
                      <img
                        src={user.profile_image.full_path}
                        alt={fullName || user.email}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-biafit-primary text-white">
                        <MdPerson className="h-6 w-6" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="mb-0.5 truncate font-medium text-navy-700 dark:text-white">
                      {fullName || "N/A"}
                    </h3>
                    <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                      {user.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      if (isDeleted) return;
                      onEdit(user);
                    }}
                    disabled={isDeleted}
                    className={`p-1 text-gray-600 transition-colors dark:text-gray-400 ${
                      isDeleted
                        ? "cursor-not-allowed opacity-50"
                        : "hover:text-blue-600 dark:hover:text-blue-400"
                    }`}
                    title="Edit User"
                  >
                    <MdEdit className="h-5 w-5" />
                  </button>
                  {/* <button
                      onClick={(event) => {
                        event.stopPropagation()
                        onDelete(user)
                      }}
                      className="p-1 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title="Delete User"
                    >
                      <MdDelete className="h-5 w-5" />
                    </button> */}
                </div>
              </div>
              <div className="my-2 flex flex-wrap items-center gap-2">
                <UserStatusBadge user={user} />
                <ActiveChallengeBadges user={user} />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Joined: {new Date(user.created_at).toLocaleDateString()}
              </div>
            </div>
          );
        })}
      />
    </Card>
  );
};

export default UsersTable;
